import { useEffect, useState } from "react";
import { MedienAnsicht, type MedienKommentar } from "../../components/medien/MedienAnsicht";
import { groesse, speichernUnter } from "../../lib/medien";
import { ZIP_MAX_BYTES, makeZip } from "../../lib/zip";
import type { Lang, ReviewComment, ReviewMedia } from "./marke-model";

/**
 * Videos, Bilder und PDF der geteilten Fassung im Freigabe-Link (0155): ansehen (Video im Player), kommentieren mit
 * Zeitmarke bzw. Punkt im Bild, einzeln herunterladen oder alles als ZIP (bis 500 MB, sonst einzeln). Jede Ausgabe steht im
 * Zugriffsprotokoll der Agentur, jede Datei trägt ihren SHA-256. Auch als Vorschau im Agentur-Bereich (ohne Kommentieren).
 */

export type MedienLink = { url: string; poster: string | null };

export function FreigabeMedien({ media, comments, lang, holen, onKommentar, name, setName }: {
  media: readonly ReviewMedia[];
  comments: readonly ReviewComment[];
  lang: Lang;
  holen: (m: ReviewMedia, download: boolean) => Promise<MedienLink>;
  /** ohne: nur ansehen (entschieden, abgelaufen, Vorschau des Teams) */
  onKommentar?: (m: ReviewMedia, k: { body: string; time_ms: number | null; x: number | null; y: number | null }) => Promise<boolean>;
  name: string;
  setName: (n: string) => void;
}) {
  const t = (de: string, en: string) => (lang === "en" ? en : de);
  const [gewaehlt, setGewaehlt] = useState(media[0]?.id ?? "");
  const [links, setLinks] = useState<Record<string, MedienLink>>({});
  const [fehler, setFehler] = useState<string | null>(null);
  const [zipBusy, setZipBusy] = useState(false);
  const m = media.find((x) => x.id === gewaehlt) ?? media[0];
  // Links gelten 15 Minuten: nach 12 Minuten verwerfen, damit die nächste Anzeige einen frischen holt
  useEffect(() => {
    const id = setInterval(() => setLinks({}), 12 * 60_000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    if (!m || links[m.id]) return;
    let aus = false;
    holen(m, false).then((l) => { if (!aus) setLinks((x) => ({ ...x, [m.id]: l })); })
      .catch(() => { if (!aus) setFehler(t("Die Datei ließ sich nicht laden.", "The file could not be loaded.")); });
    return () => { aus = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m?.id]);
  if (!m) return null;
  const summe = media.reduce((s, x) => s + x.size, 0);
  const laden = async (x: ReviewMedia) => {
    try {
      const l = await holen(x, true);
      speichernUnter(l.url, x.name);
    } catch {
      setFehler(t("Der Download ließ sich nicht starten.", "The download could not be started."));
    }
  };
  const zip = async () => {
    setZipBusy(true);
    setFehler(null);
    try {
      const files = [];
      for (const x of media) {
        const l = await holen(x, true);
        const res = await fetch(l.url);
        if (!res.ok) throw new Error(String(res.status));
        files.push({ name: x.name, data: await res.blob() });
      }
      const blob = await makeZip(files);
      const u = URL.createObjectURL(blob);
      speichernUnter(u, "content-freigabe.zip");
      setTimeout(() => URL.revokeObjectURL(u), 30_000);
    } catch {
      setFehler(t("Das ZIP-Paket ließ sich nicht erstellen. Bitte die Dateien einzeln herunterladen.", "The ZIP package could not be created. Please download the files one by one."));
    } finally {
      setZipBusy(false);
    }
  };
  const kommentare: MedienKommentar[] = comments.filter((c) => c.media_id === m.id)
    .map((c) => ({ id: c.id, time_ms: c.time_ms, x: c.x, y: c.y, body: c.body, side: "brand", name: c.name, at: c.at }));
  return (
    <section className="panel stack" aria-labelledby="freigabe-medien">
      <div className="row between">
        <h2 id="freigabe-medien">{t("Videos und Bilder", "Videos and images")}</h2>
        {media.length > 1 && (summe <= ZIP_MAX_BYTES
          ? <button type="button" onClick={() => void zip()} disabled={zipBusy} aria-busy={zipBusy}>{t(`Alle als ZIP (${groesse(summe, lang)})`, `All as ZIP (${groesse(summe, lang)})`)}</button>
          : <small className="muted">{t("Zusammen über 500 MB: bitte einzeln herunterladen.", "Over 500 MB in total: please download one by one.")}</small>)}
      </div>
      {media.length > 1 && (
        <div className="co-dateireiter" role="tablist" aria-label={t("Dateien", "Files")}>
          {media.map((x, i) => (
            <button key={x.id} type="button" role="tab" aria-selected={x.id === m.id} className={x.id === m.id ? "aktiv" : ""} onClick={() => setGewaehlt(x.id)}>
              {i + 1}. {x.name}
            </button>
          ))}
        </div>
      )}
      {onKommentar && (
        <label className="co-name">{t("Ihr Name (für Kommentare und Entscheidung)", "Your name (for comments and decision)")}
          <input value={name} maxLength={120} autoComplete="name" onChange={(e) => setName(e.target.value)} />
        </label>
      )}
      <MedienAnsicht onLadefehler={() => setLinks((x) => { const n = { ...x }; delete n[m.id]; return n; })} datei={{ id: m.id, name: m.name, kind: m.kind, size: m.size, sha256: m.sha256, duration_ms: m.duration_ms, width: m.width, height: m.height }}
        url={links[m.id]?.url ?? null} poster={links[m.id]?.poster ?? null} kommentare={kommentare} t={t}
        seiten={{ staff: t("Agentur", "Agency"), talent: "Creator", brand: t("Marke", "Brand") }}
        onKommentar={onKommentar ? (k) => {
          if (name.trim().length < 2) {
            setFehler(t("Bitte zuerst Ihren Namen angeben.", "Please enter your name first."));
            return Promise.resolve(false);
          }
          setFehler(null);
          return onKommentar(m, k);
        } : undefined} />
      <div className="row">
        <button type="button" onClick={() => void laden(m)}>{t("Herunterladen", "Download")}</button>
        <small className="muted">{t("Jeder Abruf steht im Protokoll der Agentur.", "Every access is logged by the agency.")}</small>
      </div>
      {fehler && <p className="tk-error" role="alert">{fehler}</p>}
    </section>
  );
}
